import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GridLayout, { useContainerWidth, type LayoutItem } from 'react-grid-layout';
import {
  api,
  ApiFailure,
  type Aggregation,
  type Bucket,
  type ChartKind,
  type Dashboard,
  type DashboardComponent,
  type Dataset,
  type Filter,
  type PivotTable,
  type Projection,
} from '../api';
import type { VizColors } from '../theme';
import { CategoryChart, ForecastChart, ShareChart, TrendChart } from './Charts';
import { Notice } from './Panels';

const COLUMNS = 12;
const ROW_HEIGHT = 62;

/**
 * The dashboard builder.
 *
 * Both predecessors had a `DashboardGrid`. DataVantageHub's rendered a fixed set
 * of demo components from `init-demo` and could not add one; DataDashMaster's
 * was a static CSS grid whose "layout" column in the database was never read
 * back. This one persists: every drag, resize, retitle and config change is
 * saved to the dashboard record, and reloading returns the same board.
 *
 * Each tile fetches its own data through the same query endpoints the Explore
 * tab uses. That is deliberate — a tile cannot show a number the rest of the
 * product would disagree with, because there is only one aggregation path.
 */

type Draft = Omit<DashboardComponent, 'id'> & { id: string };

interface Palette {
  kind: DashboardComponent['kind'];
  chartType?: ChartKind;
  label: string;
  what: string;
  glyph: string;
  size: { w: number; h: number };
}

const PALETTE: Palette[] = [
  { kind: 'scorecard', label: 'Scorecard', what: 'One aggregated number', glyph: '▣', size: { w: 3, h: 2 } },
  { kind: 'chart', chartType: 'bar', label: 'Bar chart', what: 'Compare across a dimension', glyph: '▥', size: { w: 6, h: 4 } },
  { kind: 'chart', chartType: 'line', label: 'Line chart', what: 'A measure over time', glyph: '📈', size: { w: 6, h: 4 } },
  { kind: 'chart', chartType: 'pie', label: 'Share', what: 'Composition of a total', glyph: '◕', size: { w: 4, h: 4 } },
  { kind: 'pivot', label: 'Pivot table', what: 'Cross-tabulate two dimensions', glyph: '▦', size: { w: 8, h: 5 } },
  { kind: 'table', label: 'Table', what: 'Grouped rows and values', glyph: '☰', size: { w: 6, h: 4 } },
  { kind: 'forecast', label: 'Forecast', what: 'Projection with interval', glyph: '⤳', size: { w: 8, h: 5 } },
];

export function DashboardBuilder({
  dataset,
  colors,
  dashboardId,
  onSaved,
}: {
  dataset: Dataset;
  colors: VizColors;
  dashboardId: string | null;
  onSaved: (dashboard: Dashboard) => void;
}) {
  const dimensions = dataset.columns.filter((c) => c.type === 'categorical' && !c.isIdentifier);
  const measures = dataset.columns.filter((c) => c.type === 'numeric' && !c.isIdentifier);
  const dates = dataset.columns.filter((c) => c.type === 'temporal');

  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [components, setComponents] = useState<Draft[]>([]);
  const [name, setName] = useState('Untitled dashboard');
  const [filters, setFilters] = useState<Filter[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [options, setOptions] = useState<Record<string, string[]>>({});

  useEffect(() => {
    api
      .filterOptions(dataset.id)
      .then((response) => setOptions(response.options))
      .catch(() => setOptions({}));
  }, [dataset.id]);

  const loadedId = useRef<string | null>(null);

  useEffect(() => {
    if (dashboardId === null) {
      setDashboard(null);
      setComponents(seed(dimensions[0]?.name, measures[0]?.name, dates[0]?.name));
      setName(`${dataset.name} dashboard`);
      setFilters([]);
      return;
    }

    // Saving a new board sets dashboardId, which used to re-run this effect and
    // overwrite whatever the user had typed in the meantime with the server's
    // now-stale copy. Only load a board we have not already got.
    if (loadedId.current === dashboardId) return;
    loadedId.current = dashboardId;

    api
      .getDashboard(dashboardId)
      .then((response) => {
        setDashboard(response.dashboard);
        setComponents(response.dashboard.components as Draft[]);
        setName(response.dashboard.name);
        setFilters(response.dashboard.filters);
      })
      .catch((error: unknown) => setFailure(asFailure(error)));
    // Seeding depends only on the dataset; re-running on every column change
    // would discard the user's board.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, dataset.id]);

  const dirty = useRef(false);

  /**
   * Debounced autosave.
   *
   * A dashboard you have to remember to save is a dashboard you lose. The delay
   * exists because a drag fires a layout change on every frame, and one PATCH
   * per frame would flood the server for no benefit.
   */
  const persist = useCallback(
    async (next: { components?: Draft[]; name?: string; filters?: Filter[] }): Promise<void> => {
      setSaveState('saving');
      try {
        if (dashboard === null) {
          const created = await api.createDashboard({
            datasetId: dataset.id,
            name: next.name ?? name,
            components: (next.components ?? components) as DashboardComponent[],
            filters: next.filters ?? filters,
          });
          setDashboard(created.dashboard);
          onSaved(created.dashboard);
        } else {
          const updated = await api.updateDashboard(dashboard.id, {
            name: next.name ?? name,
            components: (next.components ?? components) as DashboardComponent[],
            filters: next.filters ?? filters,
          });
          setDashboard(updated.dashboard);
          onSaved(updated.dashboard);
        }
        setSaveState('saved');
        setFailure(null);
      } catch (error) {
        setSaveState('idle');
        setFailure(asFailure(error));
      }
    },
    [dashboard, dataset.id, name, components, filters, onSaved],
  );

  useEffect(() => {
    if (!dirty.current) return;
    const timer = setTimeout(() => {
      dirty.current = false;
      void persist({});
    }, 900);
    return () => clearTimeout(timer);
  }, [components, name, filters, persist]);

  function mutate(next: Draft[]): void {
    dirty.current = true;
    setComponents(next);
  }

  function add(entry: Palette): void {
    const id = `c${Date.now().toString(36)}`;
    const y = components.reduce((max, c) => Math.max(max, c.layout.y + c.layout.h), 0);

    mutate([
      ...components,
      {
        id,
        kind: entry.kind,
        title: defaultTitle(entry, dimensions[0]?.name, measures[0]?.name),
        layout: { x: 0, y, ...entry.size },
        config: defaultConfig(entry, dimensions[0]?.name, measures[0]?.name, dates[0]?.name),
      },
    ]);
    setSelected(id);
  }

  const layout: LayoutItem[] = useMemo(
    () =>
      components.map((component) => ({
        i: component.id,
        x: component.layout.x,
        y: component.layout.y,
        w: component.layout.w,
        h: component.layout.h,
        minW: 2,
        minH: 2,
      })),
    [components],
  );

  // v2 replaced the WidthProvider HOC with a hook. The grid needs a pixel width
  // to compute column positions, and measuring the container is the only way to
  // get one that survives a sidebar collapse or a window resize.
  //
  // The hook's ref is nullable while React's `ref` prop is not, which is a
  // types-only mismatch — the element is attached before any measurement runs.
  const { width, containerRef } = useContainerWidth();
  const canvasRef = containerRef as React.Ref<HTMLDivElement>;

  const active = components.find((c) => c.id === selected) ?? null;

  return (
    <div className="page">
      <header className="intro">
        <h2>Dashboard</h2>
        <p>
          Drag to move, drag the corner to resize, click a tile to configure it. Changes save
          automatically.
        </p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      <div className="controls">
        <label className="field grow">
          Name
          <input
            type="text"
            value={name}
            onChange={(e) => {
              dirty.current = true;
              setName(e.target.value);
            }}
          />
        </label>

        <label className="field">
          Filter
          <select
            value={filters[0]?.column ?? ''}
            onChange={(e) => {
              dirty.current = true;
              setFilters(e.target.value === '' ? [] : [{ column: e.target.value, operator: 'equals', value: '' }]);
            }}
          >
            <option value="">No filter</option>
            {dimensions.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        {filters[0] !== undefined && (
          <label className="field">
            is
            <select
              value={String(filters[0].value ?? '')}
              onChange={(e) => {
                dirty.current = true;
                setFilters([{ ...filters[0]!, value: e.target.value }]);
              }}
            >
              <option value="">Any</option>
              {(options[filters[0].column] ?? []).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        )}

        <span className="muted" style={{ marginLeft: 'auto' }}>
          {saveState === 'saving' ? (
            <>
              <span className="spinner" /> saving
            </>
          ) : saveState === 'saved' ? (
            'all changes saved'
          ) : (
            ''
          )}
        </span>
      </div>

      <div className={active === null ? 'builder no-panel' : 'builder'}>
        <div>
          <div className="row" style={{ marginBottom: 10 }}>
            {PALETTE.map((entry) => (
              <button
                key={entry.label}
                className="ghost tiny"
                onClick={() => add(entry)}
                title={entry.what}
              >
                <span aria-hidden>{entry.glyph}</span> {entry.label}
              </button>
            ))}
          </div>

          {components.length === 0 ? (
            <div className="card empty">
              <h3>An empty canvas</h3>
              <p>Add a tile from the buttons above. Each one queries this dataset directly.</p>
            </div>
          ) : (
            <div className="canvas" ref={canvasRef}>
              {/* v2 groups the knobs into config objects rather than a flat
                  prop list: grid measurements, drag behaviour and resize
                  behaviour each have their own. */}
              <GridLayout
                className="layout"
                width={width}
                layout={layout}
                gridConfig={{ cols: COLUMNS, rowHeight: ROW_HEIGHT, margin: [10, 10] }}
                dragConfig={{ enabled: true, handle: '.tile > header' }}
                resizeConfig={{ enabled: true }}
                onLayoutChange={(next: readonly LayoutItem[]) => {
                  // Compare before writing: react-grid-layout fires this on
                  // mount and on every width change, and writing unconditionally
                  // would mark a freshly-loaded board dirty and re-save it.
                  const changed = next.some((item) => {
                    const current = components.find((c) => c.id === item.i);
                    return (
                      current !== undefined &&
                      (current.layout.x !== item.x ||
                        current.layout.y !== item.y ||
                        current.layout.w !== item.w ||
                        current.layout.h !== item.h)
                    );
                  });
                  if (!changed) return;

                  mutate(
                    components.map((component) => {
                      const item = next.find((l) => l.i === component.id);
                      return item === undefined
                        ? component
                        : { ...component, layout: { x: item.x, y: item.y, w: item.w, h: item.h } };
                    }),
                  );
                }}
              >
                {components.map((component) => (
                  <div key={component.id}>
                    <Tile
                      component={component}
                      dataset={dataset}
                      filters={filters}
                      colors={colors}
                      selected={selected === component.id}
                      onSelect={() => setSelected(component.id)}
                      onRemove={() => {
                        mutate(components.filter((c) => c.id !== component.id));
                        if (selected === component.id) setSelected(null);
                      }}
                    />
                  </div>
                ))}
              </GridLayout>
            </div>
          )}
        </div>

        {active !== null && (
          <aside className="card">
            <header>
              <h2>Configure</h2>
              <button className="ghost tiny" onClick={() => setSelected(null)}>
                ✕
              </button>
            </header>

            <TileEditor
              component={active}
              dimensions={dimensions.map((c) => c.name)}
              measures={measures.map((c) => c.name)}
              dates={dates.map((c) => c.name)}
              onChange={(next) =>
                mutate(components.map((c) => (c.id === next.id ? next : c)))
              }
            />

            <button
              className="ghost danger"
              style={{ marginTop: 14, width: '100%' }}
              onClick={() => {
                mutate(components.filter((c) => c.id !== active.id));
                setSelected(null);
              }}
            >
              Remove tile
            </button>
          </aside>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tiles
// -----------------------------------------------------------------------------

function Tile({
  component,
  dataset,
  filters,
  colors,
  selected,
  onSelect,
  onRemove,
}: {
  component: Draft;
  dataset: Dataset;
  filters: Filter[];
  colors: VizColors;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) {
  const config = component.config as Record<string, string | number | undefined>;
  const active = useMemo(
    () => filters.filter((f) => f.value !== '' && f.value !== undefined),
    [filters],
  );

  const [data, setData] = useState<
    | { kind: 'buckets'; buckets: Bucket[]; other: Bucket | null }
    | { kind: 'pivot'; pivot: PivotTable }
    | { kind: 'forecast'; projection: Projection }
    | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  const key = JSON.stringify({ config, active, kind: component.kind });

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setError(null);

    void (async () => {
      try {
        if (component.kind === 'pivot') {
          const response = await api.pivot(dataset.id, {
            filters: active,
            rowFields: [String(config['rowField'] ?? '')],
            ...(config['columnField'] === undefined || config['columnField'] === ''
              ? {}
              : { columnField: String(config['columnField']) }),
            ...(config['aggregation'] === 'count' ? {} : { measure: String(config['measure'] ?? '') }),
            aggregation: (config['aggregation'] as Aggregation) ?? 'sum',
            rowLimit: 30,
            columnLimit: 12,
          });
          if (!cancelled) setData({ kind: 'pivot', pivot: response.pivot });
          return;
        }

        if (component.kind === 'forecast') {
          const response = await api.forecast(dataset.id, {
            filters: active,
            timeColumn: String(config['timeColumn'] ?? ''),
            ...(config['aggregation'] === 'count' ? {} : { measure: String(config['measure'] ?? '') }),
            aggregation: (config['aggregation'] as Aggregation) ?? 'sum',
            horizon: Number(config['horizon'] ?? 12),
          });
          if (!cancelled) setData({ kind: 'forecast', projection: response.forecast });
          return;
        }

        const groupBy =
          component.kind === 'scorecard'
            ? undefined
            : String(config['groupBy'] ?? config['timeColumn'] ?? '');

        const response = await api.query(dataset.id, {
          filters: active,
          ...(groupBy === undefined || groupBy === '' ? {} : { groupBy }),
          ...(config['aggregation'] === 'count' ? {} : { measure: String(config['measure'] ?? '') }),
          aggregation: (config['aggregation'] as Aggregation) ?? 'sum',
          limit: component.kind === 'table' ? 50 : 12,
          sort: config['chartType'] === 'line' ? 'keyAsc' : 'valueDesc',
          ...(config['chartType'] === 'line' && config['period'] !== undefined
            ? { period: config['period'] as 'day' | 'week' | 'month' | 'quarter' | 'year' }
            : {}),
        });

        if (!cancelled) {
          setData({
            kind: 'buckets',
            buckets: response.result.buckets,
            other: response.result.otherBucket,
          });
        }
      } catch (failure) {
        if (!cancelled) {
          setData(null);
          setError(failure instanceof ApiFailure ? failure.message : 'This tile could not load.');
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset.id, key]);

  const measureLabel =
    config['aggregation'] === 'count'
      ? 'rows'
      : `${String(config['aggregation'] ?? 'sum')}(${String(config['measure'] ?? '')})`;

  return (
    <div className={selected ? 'tile selected' : 'tile'} onMouseDown={onSelect}>
      <header>
        <span className="title">{component.title}</span>
        {busy && <span className="spinner" />}
        <button
          className="ghost tiny"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={onRemove}
          aria-label="Remove tile"
        >
          ✕
        </button>
      </header>

      <div className="body">
        {error !== null && <p className="muted">{error}</p>}

        {data?.kind === 'buckets' && component.kind === 'scorecard' && (
          <div className="tile-scorecard">
            <div className="value">{formatValue(data.buckets[0]?.value ?? 0)}</div>
            <div className="muted">{measureLabel}</div>
          </div>
        )}

        {data?.kind === 'buckets' && component.kind === 'chart' && (
          <ChartBody
            chartType={(config['chartType'] as ChartKind) ?? 'bar'}
            buckets={data.buckets}
            other={data.other}
            measureLabel={measureLabel}
            colors={colors}
            height={Math.max(120, component.layout.h * ROW_HEIGHT - 76)}
          />
        )}

        {data?.kind === 'buckets' && component.kind === 'table' && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{String(config['groupBy'] ?? 'Group')}</th>
                  <th className="num">{measureLabel}</th>
                  <th className="num">Rows</th>
                </tr>
              </thead>
              <tbody>
                {data.buckets.map((bucket) => (
                  <tr key={bucket.key}>
                    <td>{bucket.key}</td>
                    <td className="num">{formatValue(bucket.value)}</td>
                    <td className="num">{bucket.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data?.kind === 'pivot' && <PivotBody pivot={data.pivot} />}

        {data?.kind === 'forecast' && (
          <ForecastChart
            forecast={data.projection}
            measureLabel={measureLabel}
            colors={colors}
            height={Math.max(140, component.layout.h * ROW_HEIGHT - 76)}
          />
        )}
      </div>
    </div>
  );
}

function ChartBody({
  chartType,
  buckets,
  other,
  measureLabel,
  colors,
  height,
}: {
  chartType: ChartKind;
  buckets: Bucket[];
  other: Bucket | null;
  measureLabel: string;
  colors: VizColors;
  height: number;
}) {
  switch (chartType) {
    case 'line':
      return <TrendChart buckets={buckets} measureLabel={measureLabel} colors={colors} height={height} />;
    case 'area':
      return (
        <TrendChart buckets={buckets} measureLabel={measureLabel} colors={colors} height={height} area />
      );
    case 'pie':
      return <ShareChart buckets={buckets} colors={colors} height={height} />;
    default:
      return (
        <CategoryChart
          buckets={buckets}
          otherBucket={other}
          measureLabel={measureLabel}
          colors={colors}
          height={height}
        />
      );
  }
}

function PivotBody({ pivot }: { pivot: PivotTable }) {
  const totalLabel = pivot.totalsAreAdditive ? 'Total' : 'Overall';

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{pivot.rowFields.join(' · ')}</th>
            {pivot.columnKeys.map((key) => (
              <th key={key} className="num">
                {key}
              </th>
            ))}
            <th className="num">{totalLabel}</th>
          </tr>
        </thead>
        <tbody>
          {pivot.rows.map((row) => (
            <tr key={row.label}>
              <td>{row.label}</td>
              {row.cells.map((cell, index) => (
                <td key={index} className={cell.value === null ? 'num blank' : 'num'}>
                  {/* An empty intersection is a dash, never 0. */}
                  {cell.value === null ? '—' : formatValue(cell.value)}
                </td>
              ))}
              <td className="num">{row.total.value === null ? '—' : formatValue(row.total.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Editor
// -----------------------------------------------------------------------------

function TileEditor({
  component,
  dimensions,
  measures,
  dates,
  onChange,
}: {
  component: Draft;
  dimensions: string[];
  measures: string[];
  dates: string[];
  onChange: (next: Draft) => void;
}) {
  const config = component.config as Record<string, string | number | undefined>;

  const set = (patch: Record<string, string | number | undefined>): void =>
    onChange({ ...component, config: { ...config, ...patch } });

  const aggregation = (
    <label className="field">
      Aggregation
      <select
        value={String(config['aggregation'] ?? 'sum')}
        onChange={(e) => set({ aggregation: e.target.value })}
      >
        {['sum', 'avg', 'count', 'countDistinct', 'min', 'max', 'median'].map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
    </label>
  );

  const measure =
    config['aggregation'] === 'count' ? null : (
      <label className="field">
        Measure
        <select value={String(config['measure'] ?? '')} onChange={(e) => set({ measure: e.target.value })}>
          {measures.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </label>
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <label className="field">
        Title
        <input
          type="text"
          value={component.title}
          onChange={(e) => onChange({ ...component, title: e.target.value })}
        />
      </label>

      {component.kind === 'chart' && (
        <label className="field">
          Chart type
          <select
            value={String(config['chartType'] ?? 'bar')}
            onChange={(e) => set({ chartType: e.target.value })}
          >
            <option value="bar">Bar</option>
            <option value="line">Line</option>
            <option value="area">Area</option>
            <option value="pie">Share</option>
          </select>
        </label>
      )}

      {(component.kind === 'chart' || component.kind === 'table') && (
        <label className="field">
          Group by
          <select value={String(config['groupBy'] ?? '')} onChange={(e) => set({ groupBy: e.target.value })}>
            {[...dimensions, ...dates].map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
      )}

      {component.kind === 'chart' &&
        config['chartType'] === 'line' &&
        dates.includes(String(config['groupBy'] ?? '')) && (
          <label className="field">
            Bucket by
            <select value={String(config['period'] ?? 'month')} onChange={(e) => set({ period: e.target.value })}>
              {['day', 'week', 'month', 'quarter', 'year'].map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        )}

      {component.kind === 'pivot' && (
        <>
          <label className="field">
            Rows
            <select value={String(config['rowField'] ?? '')} onChange={(e) => set({ rowField: e.target.value })}>
              {dimensions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Columns
            <select
              value={String(config['columnField'] ?? '')}
              onChange={(e) => set({ columnField: e.target.value })}
            >
              <option value="">(none)</option>
              {dimensions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
        </>
      )}

      {component.kind === 'forecast' && (
        <>
          <label className="field">
            Date column
            <select
              value={String(config['timeColumn'] ?? '')}
              onChange={(e) => set({ timeColumn: e.target.value })}
            >
              {dates.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Horizon
            <select value={String(config['horizon'] ?? 12)} onChange={(e) => set({ horizon: Number(e.target.value) })}>
              {[6, 12, 24].map((h) => (
                <option key={h} value={h}>
                  {h} periods
                </option>
              ))}
            </select>
          </label>
        </>
      )}

      {aggregation}
      {measure}
    </div>
  );
}

// -----------------------------------------------------------------------------

/** A first board that is already useful, built from what the dataset contains. */
function seed(dimension: string | undefined, measure: string | undefined, date: string | undefined): Draft[] {
  const out: Draft[] = [];

  if (measure !== undefined) {
    out.push({
      id: 'seed-total',
      kind: 'scorecard',
      title: `Total ${measure}`,
      layout: { x: 0, y: 0, w: 3, h: 2 },
      config: { aggregation: 'sum', measure },
    });
    out.push({
      id: 'seed-rows',
      kind: 'scorecard',
      title: 'Records',
      layout: { x: 3, y: 0, w: 3, h: 2 },
      config: { aggregation: 'count' },
    });
  }

  if (date !== undefined && measure !== undefined) {
    out.push({
      id: 'seed-trend',
      kind: 'chart',
      title: `${measure} over time`,
      layout: { x: 6, y: 0, w: 6, h: 4 },
      config: { chartType: 'line', groupBy: date, measure, aggregation: 'sum', period: 'month' },
    });
  }

  if (dimension !== undefined && measure !== undefined) {
    out.push({
      id: 'seed-breakdown',
      kind: 'chart',
      title: `${measure} by ${dimension}`,
      layout: { x: 0, y: 2, w: 6, h: 4 },
      config: { chartType: 'bar', groupBy: dimension, measure, aggregation: 'sum' },
    });
  }

  return out;
}

function defaultTitle(entry: Palette, dimension: string | undefined, measure: string | undefined): string {
  if (entry.kind === 'scorecard') return measure === undefined ? 'Records' : `Total ${measure}`;
  if (entry.kind === 'forecast') return measure === undefined ? 'Forecast' : `${measure} forecast`;
  if (measure !== undefined && dimension !== undefined) return `${measure} by ${dimension}`;
  return entry.label;
}

function defaultConfig(
  entry: Palette,
  dimension: string | undefined,
  measure: string | undefined,
  date: string | undefined,
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    aggregation: measure === undefined ? 'count' : 'sum',
    ...(measure === undefined ? {} : { measure }),
  };

  switch (entry.kind) {
    case 'chart':
      return {
        ...base,
        chartType: entry.chartType ?? 'bar',
        groupBy: entry.chartType === 'line' ? (date ?? dimension ?? '') : (dimension ?? date ?? ''),
        ...(entry.chartType === 'line' ? { period: 'month' } : {}),
      };
    case 'table':
      return { ...base, groupBy: dimension ?? date ?? '' };
    case 'pivot':
      return { ...base, rowField: dimension ?? '', columnField: '' };
    case 'forecast':
      return { ...base, timeColumn: date ?? '', horizon: 12 };
    default:
      return base;
  }
}

function formatValue(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function asFailure(error: unknown): ApiFailure {
  return error instanceof ApiFailure ? error : new ApiFailure('The request failed.', 'Try again.', 0);
}
